"use strict";

/**
 * Modal System
 *
 * A reusable modal system that supports:
 * - Data attributes with JSON for content
 * - Close button
 * - Smooth fade animations
 * - Overlay click to close
 * - Reusable for multiple scenarios
 */

const Modal = (() => {
  let overlay = null;
  let modal = null;
  let closeButton = null;
  let contentSlot = null; // Single inner slot: whatever generateModalContent returns goes here
  let isAnimating = false;

  /**
   * Initialize modal structure (base is always the same)
   */
  function init() {
    if (overlay) return; // Already initialized

    // Create overlay
    overlay = document.createElement("div");
    overlay.className = "js-modal-overlay";

    // Create modal container
    modal = document.createElement("div");
    modal.className = "modal";

    // Create close button
    closeButton = document.createElement("button");
    closeButton.className = "modal__close";
    closeButton.setAttribute("aria-label", "Close modal");
    closeButton.innerHTML = "×";
    closeButton.type = "button";

    // Single content slot: inner part is filled by generateModalContent
    contentSlot = document.createElement("div");
    contentSlot.className = "modal__content";

    // Assemble: overlay > modal > [close, contentSlot]
    modal.appendChild(closeButton);
    modal.appendChild(contentSlot);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    closeButton.addEventListener("click", hide);
    overlay.addEventListener("click", handleOverlayClick);
    document.addEventListener("keydown", handleKeyDown);
  }

  /**
   * Handle overlay click - close if clicking overlay, not modal
   */
  function handleOverlayClick(e) {
    if (e.target === overlay) {
      hide();
    }
  }

  /**
   * Handle keyboard events
   */
  function handleKeyDown(e) {
    if (
      e.key === "Escape" &&
      overlay &&
      overlay.classList.contains("is-visible")
    ) {
      hide();
    }
  }

  /**
   * Parse JSON from data attribute safely
   */
  function parseModalData(dataString) {
    try {
      return JSON.parse(dataString);
    } catch (e) {
      console.error("Invalid modal data JSON:", e);
      return { title: "", content: "" };
    }
  }

  /**
   * Generate modal inner HTML from data attribute (template literal).
   * Can be overridden for custom content per use case.
   * @param {Object} data - Parsed data from data-modal attribute
   * @returns {string} HTML string for the modal inner content
   */
  function generateModalContent(data) {
    const {
      title,
      full_name,
      role,
      company,
      web_page,
      phone,
      phone_title,
      address,
      content,
      logo,
      email,
      email_title,
      membership_type,
      industry,
    } = data;

    return `
       <div class="modal__inner">
         <div class="row">
            <div class="col col-12 col-lg-5 col-left">
               ${logo || ""}
               <h3 class="modal__title">${title || ""}</h3>
               <h4 class="modal__full_name">${full_name || ""}</h3>
               ${role ? `<p class="modal__role"> ${role || ""} </p>` : ""}
               ${company ? `<p class="modal__company modal__info-item"><strong>Company:</strong> ${company || ""} </p>` : ""}
               ${industry || membership_type ? `<p class="modal__industry-membership">${membership_type || ""} ${membership_type && industry ? " - " : ""} ${industry || ""} </p>` : ""}
               ${address ? `<p class="modal__address modal__info-item"><strong>Address:</strong> ${address || ""} </p>` : ""}
               ${phone ? `<p class="modal__phone modal__info-item"><strong>Phone:</strong> <a href="${phone}">${phone_title || phone}</a> </p>` : ""}
               ${email ? `<p class="modal__email modal__info-item"><strong>Email:</strong> <a href="${email}">${email_title || email}</a> </p>` : ""}
               ${web_page ? `<p class="modal__web_page modal__info-item"><strong>Web page:</strong> <a href="${web_page || ""}" target="_blank"> ${web_page || ""} </a> </p>` : ""}
            </div>
            <div class="col col-12 col-lg-7 col-right"
            ><div class="col-wrapper">${content || ""}</div></div>
         </div>
       </div>
    `.trim();
  }

  /**
   * Show modal with inner HTML (from generateModalContent or custom template).
   * @param {string} innerHTML - HTML string to inject into the modal content slot
   */
  function show(innerHTML) {
    if (isAnimating) return; // Prevent rapid open/close

    init(); // Ensure modal is initialized

    contentSlot.innerHTML = typeof innerHTML === "string" ? innerHTML : "";

    // Force reflow so the browser paints initial state before transition (fixes missing animation on first open)
    void overlay.offsetHeight;

    // Show modal
    isAnimating = true;
    overlay.classList.add("is-visible");
    document.documentElement.classList.add("modal-open");
    document.body.classList.add("modal-open");

    // Reset animation flag after transition
    setTimeout(() => {
      isAnimating = false;
    }, 300); // Match CSS transition duration
  }

  /**
   * Hide modal
   */
  function hide() {
    if (isAnimating || !overlay || !overlay.classList.contains("is-visible")) {
      return;
    }

    isAnimating = true;
    overlay.classList.remove("is-visible");
    document.documentElement.classList.remove("modal-open");
    document.body.classList.remove("modal-open");

    const url = new URL(window.location.href);
    url.searchParams.delete("modal-id");
    window.history.replaceState({}, "", url);

    // Reset animation flag after transition
    setTimeout(() => {
      isAnimating = false;
    }, 300); // Match CSS transition duration
  }

  /**
   * Open modal for a trigger element.
   * @param {HTMLElement} trigger - Element with data-modal (and optionally id)
   * @param {boolean} setUrlParam - Whether to set ?modal-id= on the URL
   */
  function openModalForTrigger(trigger, setUrlParam) {
    const dataString = trigger.getAttribute("data-modal");
    if (!dataString) return;

    if (setUrlParam) {
      const targetId = trigger.getAttribute("id");
      if (targetId) {
        const url = new URL(window.location.href);
        url.searchParams.set("modal-id", targetId);
        window.history.replaceState({}, "", url);
      }
    }

    const dataFromAttribute = parseModalData(dataString);
    const modalContent = generateModalContent(dataFromAttribute);
    show(modalContent);
  }

  /**
   * Auto-initialize: Find all elements with data-modal and attach handlers
   */
  function autoInit() {
    const triggers = document.querySelectorAll("[data-modal]");
    triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        openModalForTrigger(trigger, true);
      });
    });

    const modalId = new URL(window.location.href).searchParams.get("modal-id");
    if (modalId) {
      const trigger = document.getElementById(modalId);
      if (trigger && trigger.hasAttribute("data-modal")) {
        openModalForTrigger(trigger, false);
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }

  // Public API
  return {
    show,
    hide,
    init: autoInit,
    generateModalContent, // Expose so it can be overridden
  };
})();

// Export for use in other modules
export default Modal;

// Also make available globally for inline use
window.Modal = Modal;
